import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { uploadMultipart } from '../services/hdpFiles';
import AuthenticatedImage from '../components/AuthenticatedImage';

const emptyEmailForm = {
  to: '',
  subject: '',
  body: '',
};

const emptyForm = {
  alias: '',
  platform: '',
  website_url: '',
  role: '',
  backstory: '',
  status: 'active',
  case_id: '',
  notes: '',
  email: '',
  username: '',
  password: '',
  autofill_cloaked: false,
};

function CloakedPhoneUsagePanel({ usage, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="card cloaked-usage-panel" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="card-body">Loading Cloaked phone usage…</div>
      </div>
    );
  }
  if (!usage) return null;

  const statusClass = usage.at_limit ? 'hdp-alert error' : 'hdp-alert success';

  return (
    <div className="card cloaked-usage-panel" style={{ marginBottom: 'var(--spacing-lg)' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title">Cloaked phone quota</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh}>
          Refresh
        </button>
      </div>
      <div className="card-body">
        <div className={`${statusClass} cloaked-usage-status`}>
          {usage.at_limit
            ? `Phone limit reached (${usage.quota_used}/${usage.quota_limit} lifetime provisions). New numbers cannot be generated.`
            : `${usage.available_to_provision} phone slot(s) available (${usage.quota_used}/${usage.quota_limit} used).`}
        </div>
        <div className="entity-kv-grid cloaked-usage-grid" style={{ marginTop: '16px' }}>
          <div><strong>Active numbers</strong><div>{usage.active_count}</div></div>
          <div><strong>Attached to identities</strong><div>{usage.attached_count}</div></div>
          <div><strong>Deleted / historical</strong><div>{usage.deleted_count}</div></div>
          <div><strong>Orphaned active</strong><div>{usage.orphaned_active_count}</div></div>
          <div><strong>Cloaked identities</strong><div>{usage.identity_count}</div></div>
          <div><strong>Lifetime used</strong><div>{usage.quota_used}</div></div>
        </div>
        {usage.active_numbers?.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div className="entity-sidebar-title">Active numbers in account</div>
            <ul className="cloaked-usage-list">
              {usage.active_numbers.map((item) => (
                <li key={item.id}>
                  <code>{item.phone_number || item.id}</code>
                  {item.enabled === false ? ' (disabled)' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {usage.attached_identities?.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div className="entity-sidebar-title">Identities with phones</div>
            <ul className="cloaked-usage-list">
              {usage.attached_identities.map((item) => (
                <li key={item.identity_id}>
                  {item.nickname || item.identity_id}
                  {item.website_url ? ` · ${item.website_url}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p style={{ marginTop: '16px', color: 'var(--hdp-text-muted)', fontSize: '13px' }}>
          {usage.note}
          {usage.at_limit && usage.support_email ? (
            <> Contact <a href={`mailto:${usage.support_email}`}>{usage.support_email}</a> to reset quota.</>
          ) : null}
        </p>
      </div>
    </div>
  );
}

const PLATFORM_ICONS = {
  'linkedin.com': 'in',
  'facebook.com': 'fb',
  'instagram.com': 'ig',
  'x.com': 'x',
  'twitter.com': 'x',
  'github.com': 'gh',
  'telegram.org': 'tg',
  'discord.com': 'dc',
  'whatsapp.com': 'wa',
};

function platformLabel(platform) {
  const value = (platform || '').replace(/^https?:\/\//, '').split('/')[0];
  return value || 'unknown';
}

function platformIcon(platform) {
  return PLATFORM_ICONS[platformLabel(platform)] || '•';
}

function cloakedProfile(persona) {
  const profile = persona.profile_data || {};
  if (profile.source !== 'cloaked') return null;

  const legacy = profile.cloaked || {};
  let platforms = profile.cloaked_identities || [];
  if (!platforms.length && legacy.identity_id) {
    platforms = [{
      identity_id: legacy.identity_id,
      platform: platformLabel(persona.platform || legacy.website_url),
      website_url: legacy.website_url || persona.platform,
      username: legacy.username,
      is_primary: true,
    }];
  }

  return {
    source: 'cloaked',
    sharedEmail: profile.shared_email || legacy.email || platforms[0]?.email || '—',
    primaryIdentityId: profile.primary_identity_id || legacy.identity_id,
    platforms,
  };
}

function credentialSummary(persona) {
  const profile = persona.profile_data || {};
  const cloaked = cloakedProfile(persona);
  if (cloaked) {
    const primary = cloaked.platforms.find((p) => p.is_primary)
      || cloaked.platforms.find((p) => String(p.identity_id) === String(cloaked.primaryIdentityId))
      || cloaked.platforms[0];
    return {
      source: 'cloaked',
      email: cloaked.sharedEmail,
      username: primary?.username || '—',
      identityId: cloaked.primaryIdentityId,
      platforms: cloaked.platforms,
    };
  }
  if (profile.source === 'manual' && profile.credentials) {
    const c = profile.credentials;
    return {
      source: 'manual',
      email: c.email || '—',
      username: c.username || '—',
      identityId: null,
      platforms: [],
    };
  }
  return null;
}

function personaToEditForm(persona) {
  const profile = persona.profile_data || {};
  const creds = profile.cloaked || profile.credentials || {};
  return {
    alias: persona.alias || '',
    platform: persona.platform || '',
    role: persona.role || '',
    backstory: persona.backstory || '',
    status: persona.status || 'active',
    notes: persona.notes || '',
    case_id: persona.case_id || '',
    username: creds.username || '',
    password: '',
  };
}

function PersonaCard({
  item,
  summary,
  photoId,
  cloakedConfigured,
  saving,
  onEdit,
  onRevealCredentials,
  onProvisionCloaked,
  onPhotoUpload,
  onDelete,
  onSendEmail,
  onInbox,
  onAddPlatform,
  onRemovePlatform,
  onViewPlatformCreds,
}) {
  const platforms = summary?.platforms || [];

  return (
    <div className="card persona-card">
      <div className="persona-card-header">
        <div className="persona-card-identity">
          <AuthenticatedImage
            fileId={photoId}
            alt={item.alias}
            className="persona-avatar"
            style={{
              width: 58,
              height: 58,
              objectFit: 'cover',
              borderRadius: 14,
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <h3 className="persona-name">{item.alias}</h3>
            {item.role && <div className="persona-role">{item.role}</div>}
          </div>
        </div>
        <span className={`status-badge ${item.status}`}>{item.status}</span>
      </div>

      <div className="persona-card-body">
        <div style={{ marginBottom: 14 }}>
          <div className="persona-meta-label">Shared Email</div>
          {summary?.source === 'cloaked' ? (
            <div className="persona-email-chip">{summary.email}</div>
          ) : summary ? (
            <div className="persona-meta-value">{summary.email}</div>
          ) : (
            <div className="persona-meta-value" style={{ color: 'var(--hdp-text-muted)' }}>None</div>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="persona-meta-label">Platforms</div>
          {platforms.length > 0 ? (
            <div className="platform-stack">
              {platforms.map((platform) => (
                <span
                  key={platform.identity_id}
                  className={`platform-chip ${platform.is_primary ? 'primary' : ''}`}
                >
                  <span>{platformIcon(platform.platform || platform.website_url)}</span>
                  <span>{platformLabel(platform.platform || platform.website_url)}</span>
                  {platform.username && (
                    <span style={{ color: 'var(--hdp-text-muted)', fontSize: 11 }}>
                      {platform.username}
                    </span>
                  )}
                  <button
                    type="button"
                    title="View credentials"
                    onClick={() => onViewPlatformCreds(item, platform)}
                  >
                    ↗
                  </button>
                  {platforms.length > 1 && (
                    <button
                      type="button"
                      title="Remove platform"
                      disabled={saving}
                      onClick={() => onRemovePlatform(item, platform)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <div className="persona-meta-value">{item.platform || '—'}</div>
          )}
        </div>

        <div style={{ marginBottom: 4 }}>
          <div className="persona-meta-label">Case</div>
          <div className="persona-meta-value">{item.case_title || 'Unassigned'}</div>
        </div>

        {item.backstory && (
          <div style={{ marginTop: 14 }}>
            <div className="persona-meta-label">Backstory</div>
            <div className="persona-meta-value" style={{ color: 'var(--hdp-text-muted)', fontSize: 12, lineHeight: 1.55 }}>
              {item.backstory.length > 140 ? `${item.backstory.slice(0, 140)}…` : item.backstory}
            </div>
          </div>
        )}

        <div className="persona-actions">
          <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={() => onEdit(item)}>
            Edit
          </button>
          {summary?.source === 'cloaked' && (
            <>
              <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={() => onInbox(item)}>
                Inbox
              </button>
              <button type="button" className="btn btn-sm btn-primary" disabled={saving} onClick={() => onSendEmail(item)}>
                Send email
              </button>
              <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={() => onAddPlatform(item)}>
                + Platform
              </button>
            </>
          )}
          {summary ? (
            <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={() => onRevealCredentials(item)}>
              View creds
            </button>
          ) : cloakedConfigured && (
            <button type="button" className="btn btn-sm btn-primary" disabled={saving} onClick={() => onProvisionCloaked(item)}>
              Cloaked
            </button>
          )}
          <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
            Photo
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => onPhotoUpload(item.persona_id, e)}
            />
          </label>
          <button type="button" className="btn btn-sm btn-danger" disabled={saving} onClick={() => onDelete(item)}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Personas() {
  const [personas, setPersonas] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [cloakedConfigured, setCloakedConfigured] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [editingPersona, setEditingPersona] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [revealedCredentials, setRevealedCredentials] = useState(null);
  const [composingPersona, setComposingPersona] = useState(null);
  const [emailForm, setEmailForm] = useState(emptyEmailForm);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [inboxPersona, setInboxPersona] = useState(null);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxNote, setInboxNote] = useState('');
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [inboxDetail, setInboxDetail] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [platformPresets, setPlatformPresets] = useState([]);
  const [addingPlatformPersona, setAddingPlatformPersona] = useState(null);
  const [platformForm, setPlatformForm] = useState({ website_url: '', username: '', password: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [personasRes, casesRes, metaRes] = await Promise.all([
        api.get('/hdp/personas'),
        api.get('/hdp/cases'),
        api.get('/hdp/meta'),
      ]);
      setPersonas(personasRes.data.personas || []);
      setCases(casesRes.data.cases || []);
      setCloakedConfigured(Boolean(metaRes.data.cloaked_configured));
      setPlatformPresets(metaRes.data.platform_presets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const uploadPhoto = async (personaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadMultipart(`/hdp/personas/${personaId}/photos`, formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        alias: form.alias,
        platform: form.platform,
        website_url: form.website_url || form.platform,
        role: form.role,
        backstory: form.backstory,
        status: form.status,
        notes: form.notes,
        case_id: form.case_id || undefined,
        autofill_cloaked: form.autofill_cloaked,
      };
      if (!form.autofill_cloaked) {
        if (form.email) payload.email = form.email;
        if (form.username) payload.username = form.username;
        if (form.password) payload.password = form.password;
      } else if (form.username || form.password) {
        payload.username = form.username || undefined;
        payload.password = form.password || undefined;
      }

      const response = await api.post('/hdp/personas', payload);
      const personaId = response.data.persona_id;
      if (photoFile && personaId) {
        await uploadPhoto(personaId, photoFile);
      }
      setShowForm(false);
      setPhotoFile(null);
      setForm(emptyForm);
      if (response.data.cloaked) {
        setSelectedPersona(response.data);
        setRevealedCredentials({ source: 'cloaked', credentials: response.data.cloaked });
      }
      loadData();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save persona';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePersonaPhotoUpload = async (personaId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      await uploadPhoto(personaId, file);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      event.target.value = '';
    }
  };

  const handleProvisionCloaked = async (persona) => {
    setError('');
    setSaving(true);
    try {
      const response = await api.post(`/hdp/personas/${persona.persona_id}/provision-cloaked`, {
        website_url: persona.platform,
      });
      setSelectedPersona(response.data);
      setRevealedCredentials({ source: 'cloaked', credentials: response.data.cloaked });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Cloaked provisioning failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRevealCredentials = async (persona, platformEntry = null) => {
    setError('');
    setSaving(true);
    try {
      const params = platformEntry?.identity_id ? { identity_id: platformEntry.identity_id } : {};
      const response = await api.get(`/hdp/personas/${persona.persona_id}/cloaked/credentials`, { params });
      setSelectedPersona(persona);
      setRevealedCredentials(response.data);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleViewPlatformCreds = (persona, platformEntry) => {
    handleRevealCredentials(persona, platformEntry);
  };

  const handleAddPlatformOpen = (persona) => {
    setAddingPlatformPersona(persona);
    setPlatformForm({ website_url: '', username: '', password: '' });
    setError('');
  };

  const handleAddPlatformSubmit = async (e) => {
    e.preventDefault();
    if (!addingPlatformPersona) return;
    setError('');
    setSaving(true);
    try {
      const payload = {
        website_url: platformForm.website_url,
        platform: platformForm.website_url,
      };
      if (platformForm.username) payload.username = platformForm.username;
      if (platformForm.password) payload.password = platformForm.password;
      const response = await api.post(
        `/hdp/personas/${addingPlatformPersona.persona_id}/cloaked/platforms`,
        payload,
      );
      setAddingPlatformPersona(null);
      setPlatformForm({ website_url: '', username: '', password: '' });
      setSelectedPersona(response.data);
      setRevealedCredentials({
        source: 'cloaked',
        credentials: response.data.platform,
        shared_email: response.data.shared_email,
        platforms: response.data.platforms,
      });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add platform');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePlatform = async (persona, platformEntry) => {
    const label = platformLabel(platformEntry.platform || platformEntry.website_url);
    if (!window.confirm(`Remove ${label} from "${persona.alias}"? The shared email stays on other platforms.`)) {
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.delete(
        `/hdp/personas/${persona.persona_id}/cloaked/platforms/${platformEntry.identity_id}`,
      );
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove platform');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (persona) => {
    setEditingPersona(persona);
    setEditForm(personaToEditForm(persona));
    setError('');
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingPersona || !editForm) return;
    setError('');
    setSaving(true);
    try {
      const payload = {
        alias: editForm.alias,
        platform: editForm.platform,
        website_url: editForm.platform,
        role: editForm.role,
        backstory: editForm.backstory,
        status: editForm.status,
        notes: editForm.notes,
        case_id: editForm.case_id || null,
      };
      if (editForm.username) payload.username = editForm.username;
      if (editForm.password) payload.password = editForm.password;

      await api.patch(`/hdp/personas/${editingPersona.persona_id}`, payload);
      setEditingPersona(null);
      setEditForm(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update persona');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmailOpen = (persona) => {
    if (credentialSummary(persona)?.source !== 'cloaked') return;
    setComposingPersona(persona);
    setEmailForm(emptyEmailForm);
    setEmailSuccess('');
    setError('');
  };

  const handleSendEmailSubmit = async (e) => {
    e.preventDefault();
    if (!composingPersona) return;
    setError('');
    setEmailSuccess('');
    setSaving(true);
    const sentFor = composingPersona;
    try {
      const response = await api.post(
        `/hdp/personas/${sentFor.persona_id}/cloaked/send-email`,
        {
          to: emailForm.to,
          subject: emailForm.subject,
          text: emailForm.body,
        },
      );
      setEmailSuccess(
        `Email sent from ${response.data.from_email || 'persona'} to ${(response.data.to || []).join(', ')}`,
      );
      setComposingPersona(null);
      setEmailForm(emptyEmailForm);
      loadData();
      if (inboxPersona?.persona_id === sentFor.persona_id) {
        handleInboxOpen(sentFor);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send email');
    } finally {
      setSaving(false);
    }
  };

  const handleInboxOpen = async (persona) => {
    if (credentialSummary(persona)?.source !== 'cloaked') return;
    setInboxPersona(persona);
    setSelectedInboxMessage(null);
    setInboxDetail(null);
    setInboxNote('');
    setError('');
    setInboxLoading(true);
    try {
      const response = await api.get(`/hdp/personas/${persona.persona_id}/cloaked/inbox`);
      setInboxMessages(response.data.messages || []);
      setInboxNote(response.data.note || '');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load inbox');
      setInboxMessages([]);
    } finally {
      setInboxLoading(false);
    }
  };

  const handleInboxSelect = async (message) => {
    if (!inboxPersona) return;
    setSelectedInboxMessage(message);
    setInboxDetail(null);
    setInboxLoading(true);
    try {
      const response = await api.get(
        `/hdp/personas/${inboxPersona.persona_id}/cloaked/inbox/${message.activity_id}`,
      );
      setInboxDetail(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load message');
    } finally {
      setInboxLoading(false);
    }
  };

  const formatMessageDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const formatRecipients = (recipients) => {
    if (!recipients?.length) return '—';
    return recipients.map((item) => item.email || item.name).filter(Boolean).join(', ');
  };

  const handleDelete = async (persona) => {
    const summary = credentialSummary(persona);
    const cloakedNote = summary?.source === 'cloaked'
      ? ` This will delete ${summary.platforms?.length || 1} linked Cloaked ${(summary.platforms?.length || 1) === 1 ? 'identity' : 'identities'}.`
      : '';
    if (!window.confirm(`Delete persona "${persona.alias}"?${cloakedNote}`)) {
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.delete(`/hdp/personas/${persona.persona_id}`);
      if (selectedPersona?.persona_id === persona.persona_id) {
        setSelectedPersona(null);
        setRevealedCredentials(null);
      }
      if (editingPersona?.persona_id === persona.persona_id) {
        setEditingPersona(null);
        setEditForm(null);
      }
      if (composingPersona?.persona_id === persona.persona_id) {
        setComposingPersona(null);
        setEmailForm(emptyEmailForm);
      }
      if (inboxPersona?.persona_id === persona.persona_id) {
        setInboxPersona(null);
        setInboxMessages([]);
        setSelectedInboxMessage(null);
        setInboxDetail(null);
      }
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete persona');
    } finally {
      setSaving(false);
    }
  };

  const creds = revealedCredentials?.credentials || revealedCredentials?.cloaked;

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Persona Manager</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + New Persona
          </button>
        </div>
      </header>

      <div className="main-body">
        {error && <div className="hdp-alert error">{error}</div>}

        {emailSuccess && <div className="hdp-alert success">{emailSuccess}</div>}

        <div className="hdp-hero-banner">
          <p className="hdp-hero-text">
            Build cover identities manually or check <strong>Autofill via Cloaked</strong> to
            provision a shared email and per-platform credentials. Add LinkedIn, Instagram, X, and
            other platforms under one persona — they all reuse the same Cloaked inbox address.
            {cloakedConfigured
              ? ' Cloaked integration is live on this server.'
              : ' Set CLOAKED_TOKEN on the server to enable autofill.'}
          </p>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="card-header">
              <h3 className="card-title">Create Persona</h3>
            </div>
            <form className="card-body" onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Alias</label>
                  <input
                    className="form-input"
                    value={form.alias}
                    onChange={(e) => setForm({ ...form, alias: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Platform / Website</label>
                  <input
                    className="form-input"
                    placeholder="linkedin.com, github.com, telegram"
                    value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value, website_url: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Role / Cover</label>
                  <input
                    className="form-input"
                    placeholder="Recruiter, contractor, victim profile"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Linked Case</label>
                  <select
                    className="form-input"
                    value={form.case_id}
                    onChange={(e) => setForm({ ...form, case_id: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {cases.map((item) => (
                      <option key={item.case_id} value={item.case_id}>{item.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.autofill_cloaked}
                    disabled={!cloakedConfigured}
                    onChange={(e) => setForm({ ...form, autofill_cloaked: e.target.checked })}
                  />
                  <span>Autofill via Cloaked (generate identity, email, username, password)</span>
                </label>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 'var(--spacing-md)',
                opacity: form.autofill_cloaked ? 0.55 : 1,
              }}>
                <div className="form-group">
                  <label className="form-label">Email {form.autofill_cloaked ? '(Cloaked generates)' : ''}</label>
                  <input
                    className="form-input"
                    value={form.email}
                    disabled={form.autofill_cloaked}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Username {form.autofill_cloaked ? '(optional override)' : ''}</label>
                  <input
                    className="form-input"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password {form.autofill_cloaked ? '(optional override)' : ''}</label>
                  <input
                    className="form-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Profile Photo</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="form-input"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Backstory</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={form.backstory}
                  onChange={(e) => setForm({ ...form, backstory: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Operational Notes</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Persona'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {editingPersona && editForm && (
          <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div className="card-header">
              <h3 className="card-title">Edit Persona — {editingPersona.alias}</h3>
            </div>
            <form className="card-body" onSubmit={handleEditSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Alias</label>
                  <input
                    className="form-input"
                    value={editForm.alias}
                    onChange={(e) => setEditForm({ ...editForm, alias: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Platform / Website</label>
                  <input
                    className="form-input"
                    value={editForm.platform}
                    onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    className="form-input"
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New password</label>
                  <input
                    className="form-input"
                    type="password"
                    placeholder="Leave blank to keep current"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="draft">draft</option>
                    <option value="active">active</option>
                    <option value="paused">paused</option>
                    <option value="retired">retired</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Role / Cover</label>
                <input
                  className="form-input"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Backstory</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={editForm.backstory}
                  onChange={(e) => setEditForm({ ...editForm, backstory: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Operational Notes</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
              {credentialSummary(editingPersona)?.source === 'cloaked' && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Changes to alias, platform, username, password, backstory, role, and notes
                  will sync to the linked Cloaked identity.
                </p>
              )}
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setEditingPersona(null); setEditForm(null); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {addingPlatformPersona && (
          <div className="hdp-panel">
            <div className="hdp-panel-header">
              <h3 className="hdp-panel-title">Add Platform — {addingPlatformPersona.alias}</h3>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => { setAddingPlatformPersona(null); setPlatformForm({ website_url: '', username: '', password: '' }); }}
              >
                Close
              </button>
            </div>
            <form className="hdp-panel-body" onSubmit={handleAddPlatformSubmit}>
              <p style={{ color: 'var(--hdp-text-muted)', fontSize: 13, marginTop: 0 }}>
                Creates a new Cloaked identity and attaches the persona&apos;s shared email.
                Username and password are unique per platform.
              </p>
              <div className="platform-presets">
                {platformPresets.map((preset) => (
                  <button
                    key={preset.website_url}
                    type="button"
                    className={`preset-chip ${platformForm.website_url === preset.website_url ? 'active' : ''}`}
                    onClick={() => setPlatformForm({ ...platformForm, website_url: preset.website_url })}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Platform / Website</label>
                  <input
                    className="form-input"
                    placeholder="instagram.com"
                    value={platformForm.website_url}
                    onChange={(e) => setPlatformForm({ ...platformForm, website_url: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Username (optional)</label>
                  <input
                    className="form-input"
                    value={platformForm.username}
                    onChange={(e) => setPlatformForm({ ...platformForm, username: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password (optional)</label>
                  <input
                    className="form-input"
                    type="password"
                    value={platformForm.password}
                    onChange={(e) => setPlatformForm({ ...platformForm, password: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Adding…' : 'Add Platform'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setAddingPlatformPersona(null); setPlatformForm({ website_url: '', username: '', password: '' }); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {composingPersona && (
          <div className="hdp-panel">
            <div className="hdp-panel-header">
              <h3 className="hdp-panel-title">Send Email — {composingPersona.alias}</h3>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => { setComposingPersona(null); setEmailForm(emptyEmailForm); }}
              >
                Close
              </button>
            </div>
            <form className="hdp-panel-body" onSubmit={handleSendEmailSubmit}>
              <p style={{ color: 'var(--hdp-text-muted)', fontSize: 13, marginTop: 0 }}>
                From:{' '}
                <span className="persona-email-chip" style={{ display: 'inline-flex', marginTop: 0 }}>
                  {credentialSummary(composingPersona)?.email || 'Cloaked persona email'}
                </span>
              </p>
              <div className="form-group">
                <label className="form-label">To</label>
                <input
                  className="form-input"
                  placeholder="recipient@example.com, other@example.com"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input
                  className="form-input"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-input"
                  rows={5}
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sending…' : 'Send Email'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setComposingPersona(null); setEmailForm(emptyEmailForm); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {inboxPersona && (
          <div className="hdp-panel">
            <div className="hdp-panel-header">
              <h3 className="hdp-panel-title">Inbox — {inboxPersona.alias}</h3>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setInboxPersona(null);
                  setInboxMessages([]);
                  setSelectedInboxMessage(null);
                  setInboxDetail(null);
                }}
              >
                Close
              </button>
            </div>
            <div className="hdp-panel-body">
              {inboxNote && (
                <p style={{ color: 'var(--hdp-text-muted)', fontSize: 12, marginTop: 0 }}>
                  {inboxNote}
                </p>
              )}
              {inboxLoading && !inboxMessages.length ? (
                <p style={{ color: 'var(--hdp-text-muted)' }}>Loading inbox…</p>
              ) : inboxMessages.length === 0 ? (
                <p style={{ color: 'var(--hdp-text-muted)' }}>No messages yet.</p>
              ) : (
                <div className="inbox-layout">
                  <div>
                    {inboxMessages.map((message) => {
                      const selected = selectedInboxMessage?.activity_id === message.activity_id;
                      return (
                        <button
                          key={message.activity_id}
                          type="button"
                          onClick={() => handleInboxSelect(message)}
                          className={`inbox-item ${selected ? 'selected' : ''}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: 6 }}>
                            <span className={`status-badge ${message.inbound ? 'active' : 'draft'}`}>
                              {message.inbound ? 'Inbound' : 'Sent'}
                            </span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                              {formatMessageDate(message.delivery_datetime)}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>
                            {message.subject}
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', wordBreak: 'break-word' }}>
                            {message.inbound ? message.sender_email : formatRecipients(message.to_recipients)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="inbox-detail">
                    {!selectedInboxMessage ? (
                      <p style={{ color: 'var(--hdp-text-muted)' }}>Select a message to view details.</p>
                    ) : inboxLoading && !inboxDetail ? (
                      <p style={{ color: 'var(--hdp-text-muted)' }}>Loading message…</p>
                    ) : (
                      <>
                        <div style={{ marginBottom: 'var(--spacing-md)' }}>
                          <div><strong>Subject</strong><br />{selectedInboxMessage.subject}</div>
                          <div style={{ marginTop: 8 }}>
                            <strong>{selectedInboxMessage.inbound ? 'From' : 'To'}</strong><br />
                            {selectedInboxMessage.inbound
                              ? `${selectedInboxMessage.sender_name || ''} ${selectedInboxMessage.sender_email || ''}`.trim()
                              : formatRecipients(selectedInboxMessage.to_recipients)}
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <strong>Date</strong><br />{formatMessageDate(selectedInboxMessage.delivery_datetime)}
                          </div>
                        </div>
                        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 'var(--spacing-md)' }}>
                          <strong>Message</strong>
                          {inboxDetail?.content?.content ? (
                            <pre style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              marginTop: 8,
                              color: 'var(--text-primary)',
                              fontFamily: 'var(--font-mono)',
                              fontSize: '12px',
                            }}>
                              {inboxDetail.content.content}
                            </pre>
                          ) : (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: 8 }}>
                              {selectedInboxMessage.sent_via_hivemind
                                ? 'Plaintext unavailable for this cached send.'
                                : 'Body is encrypted by Cloaked end-to-end. Open my.cloaked.com to decrypt inbound messages.'}
                            </p>
                          )}
                          {selectedInboxMessage.has_attachments && (
                            <div style={{ marginTop: 'var(--spacing-md)' }}>
                              <strong>Attachments</strong>
                              <ul style={{ margin: '8px 0 0', paddingLeft: '1.2rem' }}>
                                {(selectedInboxMessage.attachments || []).map((file) => (
                                  <li key={file.id || file.name} style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                                    {file.name || file.file_name}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {revealedCredentials && creds && (
          <div className="hdp-panel">
            <div className="hdp-panel-header">
              <h3 className="hdp-panel-title">
                Credentials — {selectedPersona?.alias || 'Persona'}
                {revealedCredentials.source === 'cloaked' && ' (Cloaked)'}
              </h3>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setRevealedCredentials(null)}>
                Close
              </button>
            </div>
            <div className="hdp-panel-body">
              {revealedCredentials.shared_email && (
                <div className="persona-email-chip" style={{ marginBottom: 16 }}>
                  Shared: {revealedCredentials.shared_email}
                </div>
              )}
              <div className="creds-grid">
                <div className="creds-field"><strong>Email</strong><code>{creds.email || revealedCredentials.shared_email || '—'}</code></div>
                <div className="creds-field"><strong>Platform</strong><code>{creds.platform || creds.website_url || '—'}</code></div>
                <div className="creds-field"><strong>Username</strong><code>{creds.username || '—'}</code></div>
                <div className="creds-field"><strong>Password</strong><code>{creds.password || '—'}</code></div>
                {creds.identity_id && (
                  <div className="creds-field"><strong>Cloaked ID</strong><code>{creds.identity_id}</code></div>
                )}
                {creds.notes && (
                  <div className="creds-field" style={{ gridColumn: '1 / -1' }}><strong>Secure notes</strong><code>{creds.notes}</code></div>
                )}
              </div>
              {revealedCredentials.platforms?.length > 1 && (
                <div style={{ marginTop: 18 }}>
                  <div className="persona-meta-label">All Platforms</div>
                  <div className="platform-stack">
                    {revealedCredentials.platforms.map((platform) => (
                      <button
                        key={platform.identity_id}
                        type="button"
                        className={`platform-chip ${platform.is_primary ? 'primary' : ''}`}
                        onClick={() => handleViewPlatformCreds(selectedPersona, platform)}
                      >
                        {platformLabel(platform.platform || platform.website_url)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading personas...</p>
        ) : personas.length === 0 ? (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <p>No personas registered yet.</p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginTop: 'var(--spacing-md)' }}
                onClick={() => setShowForm(true)}
              >
                Create First Persona
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="hdp-section-head">
              <h3 className="hdp-section-title">Persona Registry</h3>
              <span style={{ color: 'var(--hdp-text-muted)', fontSize: 12 }}>
                {personas.length} persona{personas.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="hdp-grid">
              {personas.map((item) => {
                const summary = credentialSummary(item);
                const photoId = item.primary_photo_id || item.primary_photo?.file_id;
                return (
                  <PersonaCard
                    key={item.persona_id}
                    item={item}
                    summary={summary}
                    photoId={photoId}
                    cloakedConfigured={cloakedConfigured}
                    saving={saving}
                    onEdit={handleEditOpen}
                    onRevealCredentials={handleRevealCredentials}
                    onProvisionCloaked={handleProvisionCloaked}
                    onPhotoUpload={handlePersonaPhotoUpload}
                    onDelete={handleDelete}
                    onSendEmail={handleSendEmailOpen}
                    onInbox={handleInboxOpen}
                    onAddPlatform={handleAddPlatformOpen}
                    onRemovePlatform={handleRemovePlatform}
                    onViewPlatformCreds={handleViewPlatformCreds}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Personas;
